import React, { useEffect, useState } from 'react';
import unfilteredNavigation from '@/core/navigation';
import usePermissions from '@/hooks/usePermissions';
import { Menu } from 'antd';
import { Link, useLocation } from 'react-router-dom';


// Sider logo sizing bounds (used by the logo-fit effect below):
//   FLOOR      - if fewer than this many px are left for the logo, hide it.
//   CAP        - never grow the logo taller than this.
//   HYSTERESIS - once hidden, require FLOOR + this much free space before showing
//                it again, so the logo does not flicker on/off at the boundary.
const SIDER_LOGO_FLOOR_PX = 45;
const SIDER_LOGO_CAP_PX = 100;
const SIDER_LOGO_HYSTERESIS_PX = 5;

// onNavigate is optional; the mobile nav drawer passes it to close itself when a
// menu link is clicked. The desktop sider passes nothing.
const LayoutNavigation = ({ onNavigate }) => {
	const permissions                     = usePermissions(),
		  [ navigation, setNavigation ]   = useState(filterNavigation(unfilteredNavigation(), permissions)),
		  [ selectedKey, setSelectedKey ] = useState([ useLocation().pathname ]);

	useEffect(() => {
		setNavigation(filterNavigation(unfilteredNavigation(), permissions));
	}, [ permissions ]);

	// Fit the sider logo into whatever vertical space is left below the nav menu,
	// and hide it entirely when there is too little room. Host SASS sizes
	// `.sider_logo` from the `--sider-logo-h` variable set here and hides it via
	// the `sider-logo-hidden` body class. A pure-CSS version was attempted and
	// could not express "size to leftover space AND disappear below a floor"
	// together, so this stays in JS.
	//
	// This is NOT a polling loop and has no idle cost. The ResizeObserver fires
	// only when the menu/container actually changes size; the MutationObserver
	// fires only when the menu's classes change (expand/collapse). The
	// requestAnimationFrame in scheduleDeferred is a coalescer, not a ticker: it
	// collapses a burst of mutations into a single evaluate() on the next frame
	// and then schedules nothing. At idle, zero callbacks run.
	useEffect(() => {
		let rafId = null;
		let lastLogoPx = -1;
		let lastHidden = false;
		let logoMarginPx = 0;

		const readLogoMargins = () => {
			const logoEl = document.querySelector('.sider_logo');
			if (!logoEl) return;
			const s = window.getComputedStyle(logoEl);
			// margin-top is auto (it pins the logo to the container bottom), so
			// only the fixed bottom margin counts as reserved space
			logoMarginPx = parseFloat(s.marginBottom) || 0;
		};

		const evaluate = () => {
			const menuEl = document.querySelector('.nav_menu');
			const parent = menuEl && menuEl.parentElement;
			if (!menuEl || !parent) return;

			const containerH = parent.clientHeight;
			const menuNatural = menuEl.scrollHeight;
			const available = containerH - menuNatural - logoMarginPx;

			const threshold = lastHidden
				? SIDER_LOGO_FLOOR_PX + SIDER_LOGO_HYSTERESIS_PX
				: SIDER_LOGO_FLOOR_PX;

			if (available < threshold) {
				if (!lastHidden) {
					document.body.classList.add('sider-logo-hidden');
					document.documentElement.style.removeProperty('--sider-logo-h');
					lastHidden = true;
					lastLogoPx = -1;
				}
				return;
			}

			const next = Math.min(SIDER_LOGO_CAP_PX, available);
			if (lastHidden || next !== lastLogoPx) {
				if (lastHidden) document.body.classList.remove('sider-logo-hidden');
				document.documentElement.style.setProperty('--sider-logo-h', next + 'px');
				lastLogoPx = next;
				lastHidden = false;
			}
		};

		const scheduleDeferred = () => {
			if (rafId == null) rafId = window.requestAnimationFrame(() => {
				rafId = null;
				evaluate();
			});
		};

		const menuEl = document.querySelector('.nav_menu');
		if (!menuEl) return;

		readLogoMargins();

		const ro = new ResizeObserver(evaluate);
		ro.observe(menuEl);
		if (menuEl.parentElement) ro.observe(menuEl.parentElement);

		const mo = new MutationObserver(scheduleDeferred);
		mo.observe(menuEl, { attributes: true, subtree: true, attributeFilter: [ 'class' ] });

		evaluate();

		return () => {
			if (rafId != null) window.cancelAnimationFrame(rafId);
			ro.disconnect();
			mo.disconnect();
			document.body.classList.remove('sider-logo-hidden');
			document.documentElement.style.removeProperty('--sider-logo-h');
		};
	}, [ navigation ]);

	const menuItems = navigation.map((nav) => {

		if(nav.single === true) {
			return {
				key:   nav.url,
				label: (<Link to={nav.url} title={nav.title}>
					{nav.title}
				</Link>),
				title:   nav.title,
				icon:    nav.icon,
				onClick: () => {
					setSelectedKey([ nav.url ]);
					onNavigate && onNavigate();
				},
			};
		}

		return {
			key:         nav.url,
			icon:        nav.icon,
			title:       nav.title,
			label:       (<span title={nav.title}>{nav.title}</span>),
			popupOffset: [ -5, 10 ],
			children: nav.children.map(child => ({
				key:   child.url,
				label: (<Link to={child.url} title={child.title}>
					{child.title}
				</Link>),
				icon:  child.icon,
				onClick: () => {
					setSelectedKey([ child.url ]);
					onNavigate && onNavigate();
				},
			})),
		};

	});


	return (
		<>
			<Menu
				mode="inline"
				className="nav_menu"
				selectedKeys={selectedKey}
				theme="dark"
				items={menuItems}
			/>
			<div className="sider_logo" alt={import.meta.env.VITE_COMPANY_NAME} />
		</>

	);
};



function filterNavigation(navigation, permissions) {
	return navigation
		.filter(row => row.top === true && hasRequiredPermissions(row.permissions, permissions))
		.map(row => ({
			...row,
			children: row.children.filter(child => hasRequiredPermissions((child.permissions || []), permissions)),
		}));
}



function hasRequiredPermissions(required, permissions) {
	for(let i = 0; i < required.length; i++) {
		let key = required[i];

		if(permissions[key] !== true) {
			return false;
		}
	}

	return true;
}


export default LayoutNavigation;
